/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  DrawInvoiceStatus,
  InvoiceType,
  isLendersScheduleCode,
  JobBasicInfoDto,
  JobDrawsDto,
  JobDrawsInvoiceDto,
  JobDrawsLendersScheduleItemDto,
  JobDrawsPaymentScheduleItemDto,
  JobDrawsRecalculateRequestDto,
  JobDrawsRuleDto,
  UserListItemDto,
} from '@gh/builder-shared-data';
import {
  EMPTY_FORM_STATE_SNAPSHOT,
  EntityConfig,
  entityValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  IS_REGION_AU_OR_NZ,
  NumberTypes,
} from '@gh/common-shared';
import { ID, WithRecalculationNeeded, WithVersion } from '@gh/common-shared-data';
import { find, first, get, isNil, omitBy, pick, sortBy, stubTrue, sumBy } from 'lodash';
export type JobBasicInfo = JobBasicInfoDto;
export type UserListItem = UserListItemDto;
export type JobDrawsRecalculateRequest = JobDrawsRecalculateRequestDto;
export type JobDrawsDetailsRule = JobDrawsRuleDto;
export type JobDrawsPaymentScheduleItem = JobDrawsPaymentScheduleItemDto;

export const createTotalLendersScheduleItem = (items: JobDrawsLendersScheduleItem[],
                                               placement: JobDrawsLenderScheduleItemPlacement,
                                               orderCost: number,
                                               orderCostPct?: number,
                                               amountDrawn?: number,
                                               balance?: number): JobDrawsLendersScheduleItem =>
  <JobDrawsLendersScheduleItem><any>({
    orderCost,
    orderCostPct,
    placement,
    amountDrawn,
    balance,
  });

export const calculateLendersScheduleTotals = (items: JobDrawsLendersScheduleItem[],
                                               totalOrderCost: number) => {
  const orderCost = sumBy(items, 'orderCost');
  const amountDrawn = sumBy(items, 'amountDrawn');
  const balance = sumBy(items, 'balance');
  return [
    createTotalLendersScheduleItem(
      items,
      JobDrawsLenderScheduleItemPlacement.Total,
      orderCost,
      orderCost ? orderCost / totalOrderCost * 100 : 0,
      amountDrawn,
      balance),
  ];
};

function hasOnlyAllowedInvoiceStatus(invoices: any[]): boolean {
  return !invoices.filter((invoice) => invoice.status !== DrawInvoiceStatus.Deleted
                                       && invoice.status !== DrawInvoiceStatus.Voided).length;
}

function createTotalInvoice(invoices: JobDrawsDetailsInvoice[],
                            placement: JobDrawsInvoicePlacement,
                            predicate: PredicateFn<JobDrawsDetailsInvoice>): JobDrawsDetailsInvoice {
  let amount = 0;
  let balance = 0;

  invoices
    .filter(predicate)
    .forEach((invoice) => {
      amount += invoice.amountWithGst || 0;
      balance += invoice.balance || 0;
    });

  return <JobDrawsDetailsInvoice>{
    invoiceId: `total:${placement}`,
    amountWithGst: amount,
    balance,
    placement,
  };
}

export const calculateInvoiceTotals = (invoices: JobDrawsDetailsInvoice[]) => [
  createTotalInvoice(
    invoices,
    JobDrawsInvoicePlacement.TotalContract,
    (invoice) => invoice.type === InvoiceType.Draw),
  createTotalInvoice(
    invoices,
    JobDrawsInvoicePlacement.TotalChangeOrders,
    (invoice) => invoice.type === InvoiceType.ChangeOrder),
  createTotalInvoice(
    invoices,
    JobDrawsInvoicePlacement.TotalDraws,
    (invoice) => invoice.type === InvoiceType.Draw || invoice.type === InvoiceType.ChangeOrder),
  createTotalInvoice(
    invoices,
    JobDrawsInvoicePlacement.TotalCrNotes,
    (invoice) => invoice.type === InvoiceType.CreditNote),
];

export enum JobDrawsLenderScheduleItemPlacement {
  Regular = 'Regular',
  Total = 'Total',
}

export type JobDrawsLendersScheduleItem = JobDrawsLendersScheduleItemDto & {
  placement: JobDrawsLenderScheduleItemPlacement;
};

export enum JobDrawsInvoicePlacement {
  Regular = 'Regular',
  TotalContract = 'TotalContract',
  TotalChangeOrders = 'TotalChangeOrders',
  TotalDraws = 'TotalDraws',
  TotalCrNotes = 'TotalCrNotes',
}

export type JobDrawsDetailsInvoice = JobDrawsInvoiceDto & {
  placement: JobDrawsInvoicePlacement;
};
export type JobDrawsDetailsInfo = {
  contractAmount: number;
  contractAmountWithGst: number;
  contractAmountLenders: number;
  contractDrawnAmount: number;
  contractToBe: number;

  variationsAmount: number;
  variationsAmountWithGst: number;
  variationsDrawnAmount: number;
  variationsToBe: number;

  totalDrawnAmount: number;
  totalAmountWithGst: number;
  totalAmount: number;
  totalToBe: number;

  isContractAmountManual: boolean;
  isContractAmountWithGstManual: boolean;
};

export enum JobDrawsDetailsTab { History = 'HISTORY', Schedule = 'SCHEDULE' }

export type JobDrawsDetails = WithRecalculationNeeded<WithVersion<{
  originalRuleId?: ID;
  currentRuleId?: ID;
  selectedInvoiceId?: ID;
  selectedTab: JobDrawsDetailsTab;
  rules: JobDrawsDetailsRule[];
  info: FormStateSnapshot<JobDrawsDetailsInfo>;
  invoices: JobDrawsDetailsInvoice[];
  paymentSchedule: JobDrawsPaymentScheduleItem[];
  lendersSchedule: JobDrawsLendersScheduleItem[];
  isLendersScheduleRefreshed: boolean;
  isDrawRuleModified: boolean;
  isNotProceeding: boolean;
  isFinalized: boolean;
}>>;

const JOB_DRAWS_DETAILS_INFO_ENTITY_CONFIG: EntityConfig<JobDrawsDetailsInfo> = omitBy(
  <any>{
    contractAmount: { required: true, type: NumberTypes.Price },
    contractAmountWithGst: IS_REGION_AU_OR_NZ ? { required: true, type: NumberTypes.Price } : void 0,
  },
  isNil);

export const validateJobDrawsDetailsInfo = entityValidatorByEntityConfig(JOB_DRAWS_DETAILS_INFO_ENTITY_CONFIG);

export const JOB_DRAWS_DETAILS_INIT: JobDrawsDetails = {
  selectedTab: JobDrawsDetailsTab.History,
  info: EMPTY_FORM_STATE_SNAPSHOT,
  rules: [],
  invoices: [],
  paymentSchedule: [],
  lendersSchedule: [],
  isLendersScheduleRefreshed: false,
  isDrawRuleModified: false,
  isNotProceeding: false,
  isFinalized: false,
};
export const invoiceNumberSorting = (invoiceA: string, invoiceB: string): number => {
  const a = invoiceA.split('').filter((simbl) => !!parseFloat(simbl) || parseFloat(simbl) === 0).join('');
  const b = invoiceB.split('').filter((simbl) => !!parseFloat(simbl) || parseFloat(simbl) === 0).join('');
  const aNumber = parseInt(a);
  const bNumber = parseInt(b);
  if (aNumber > bNumber) {
    return 1;
  }
  if (aNumber < bNumber) {
    return -1;
  }
  return 0;
};

export const jobDrawsDetailsFromDto = (dto: JobDrawsDto): JobDrawsDetails => {
  const invoices = sortBy(dto.invoices, 'accountingDate').map((invoice) => ({
    ...invoice,
    payments: sortBy(invoice.payments, 'reference'),
    placement: JobDrawsInvoicePlacement.Regular,
  }));

  const lenderScheduleOrderCostTotal = sumBy(dto.lendersSchedule, 'orderCost');
  const currentRule = find(dto.drawRules, { id: dto.drawRuleId });

  return {
    originalRuleId: dto.drawRuleId,
    currentRuleId: dto.drawRuleId,
    selectedTab: JobDrawsDetailsTab.History,
    info: formStateSnapshot({
      ...pick(dto, [
        'contractAmount', 'contractAmountLenders', 'contractAmountWithGst', 'contractDrawnAmount', 'contractToBe',
        'variationsAmount', 'variationsAmountWithGst', 'variationsDrawnAmount', 'variationsToBe',
        'totalDrawnAmount', 'totalAmountWithGst', 'totalAmount', 'totalToBe',
      ]),
      isContractAmountManual: false,
      isContractAmountWithGstManual: false,
    }),
    rules: dto.drawRules
      // include lender's schedule draw rule only if it is allowed and job  have invoices only with statuses 4 (DELETED), 5 (VOIDED)
      .filter(dto.isLenderScheduleAllowed && hasOnlyAllowedInvoiceStatus(invoices)
              || currentRule && isLendersScheduleCode(currentRule.code)
        ? stubTrue
        : (rule) => !isLendersScheduleCode(rule.code)),
    invoices,
    paymentSchedule: dto.paymentSchedule,
    lendersSchedule: dto.lendersSchedule.map((item) => ({
      ...item,
      orderCostPct: item.orderCost / lenderScheduleOrderCostTotal * 100,
      placement: JobDrawsLenderScheduleItemPlacement.Regular,
    })),
    selectedInvoiceId: get(first(invoices), 'invoiceId'),
    isLendersScheduleRefreshed: false,
    isDrawRuleModified: false,
    isNotProceeding: dto.isNotProceeding,
    isFinalized: dto.isFinalized,
    version: dto.version,
  };
};

export const getJobDrawsInvoiceId = (invoice: JobDrawsDetailsInvoice) => invoice.invoiceId;
